<template>
  <div class="relative">
    <img :src="audioImage" class="w-full h-full mb-[24px]" />
    <audio ref="audioPlayer" controls controlsList="nodownload" :src="audioUrl" class="w-full">
      Your browser does not support the <code>audio</code> element.
    </audio>
  </div>
</template>

<script>
import { trackSermonPlayback } from '@/utils/analytics';

export default {
  props: {
    audioUrl: {
      type: String,
      required: true,
    },
    audioImage: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      hasTrackedAudioStart: false,
    };
  },
  watch: {
    audioUrl() {
      this.hasTrackedAudioStart = false;
    },
  },
  mounted() {
    const audioElement = this.$refs.audioPlayer;
    if (audioElement) {
      audioElement.addEventListener('play', this.onAudioPlay);
      this.setupMediaSession();
    }
  },
  beforeUnmount() {
    const audioElement = this.$refs.audioPlayer;
    if (audioElement) {
      audioElement.removeEventListener('play', this.onAudioPlay);
    }
    this.cleanupMediaSession();
  },
  methods: {
    setupMediaSession() {
      if (!('mediaSession' in navigator)) {
        return;
      }

      const audioElement = () => this.$refs.audioPlayer;

      navigator.mediaSession.setActionHandler('play', () => {
        const el = audioElement();
        if (el?.paused) {
          el.play();
        }
      });

      navigator.mediaSession.setActionHandler('pause', () => {
        const el = audioElement();
        if (el && !el.paused) {
          el.pause();
        }
      });
    },
    updateMediaSessionMetadata() {
      if (!('mediaSession' in navigator) || !this.$refs.audioPlayer) {
        return;
      }

      const artwork = this.loadArtwork();
      const metadata = {
        title: this.title,
        artist: 'HOGC',
        artwork: artwork,
      };

      navigator.mediaSession.metadata = new MediaMetadata(metadata);
    },
    loadArtwork() {
      if (!this.audioImage) return [];

      const ARTWORK_SIZES = ['512x512', '256x256', '128x128'];

      return ARTWORK_SIZES.map(size => ({
        src: this.audioImage,
        sizes: size,
        type: 'image/jpeg',
      }));
    },

    pause() {
      const audioElement = this.$refs.audioPlayer;
      audioElement?.pause();
    },

    cleanupMediaSession() {
      if (!('mediaSession' in navigator)) {
        return;
      }

      // Clear metadata
      navigator.mediaSession.metadata = null;

      // Remove all action handlers
      try {
        navigator.mediaSession.setActionHandler('play', null);
        navigator.mediaSession.setActionHandler('pause', null);
      } catch (error) {
        // Some browsers may not support removing handlers
        console.warn('Failed to remove Media Session handlers:', error);
      }
    },
    onAudioPlay() {
      // Update Media Session metadata when audio starts playing
      this.updateMediaSessionMetadata();

      if (this.hasTrackedAudioStart) return;

      this.hasTrackedAudioStart = true;
      trackSermonPlayback({
        action: 'started',
        sermonId: this.title,
        title: this.title,
        source: 'sermon-audio-player',
      });
    },
  },
};
</script>
